import React, { useState, useEffect, useCallback } from 'react';
import './CouponAnalysis.css';

const API_BASE = '/api';

function CouponAnalysis() {
  const _now = new Date();
  const _y = _now.getFullYear(), _m = String(_now.getMonth() + 1).padStart(2, '0'), _d = String(_now.getDate()).padStart(2, '0');
  const [startDate, setStartDate] = useState(`${_y}-${_m}-01`);
  const [endDate, setEndDate] = useState(`${_y}-${_m}-${_d}`);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('player');
  const [sortField, setSortField] = useState('net');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [displayNameMap, setDisplayNameMap] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [excludeSmall, setExcludeSmall] = useState(true);
  const [royalOnly, setRoyalOnly] = useState(false);
  const [subTab, setSubTab] = useState({});
  const [subExpanded, setSubExpanded] = useState({});

  const token = localStorage.getItem('token');

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/coupons/users`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (d.success) {
        setUsers(d.users || []);
        setDisplayNameMap(d.displayNameMap || {});
      }
    } catch {}
  }, [token]);

  const fetchAnalysis = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ startDate, endDate });
      if (selectedUsers.length > 0) params.append('nesineUser', selectedUsers.join(','));
      if (excludeSmall) params.append('minAmount', '1000');
      if (royalOnly) params.append('royalOnly', '1');
      else params.append('excludeRoyal', '1');
      const res = await fetch(`${API_BASE}/coupons/analysis?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (d.success) { setData(d.data); setExpanded({}); }
    } catch {}
    setLoading(false);
  }, [startDate, endDate, selectedUsers, excludeSmall, royalOnly, token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

  const formatMoney = (v) => {
    const num = typeof v === 'number' ? v : parseFloat(v) || 0;
    return num.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const parseTL = (s) => {
    if (!s) return 0;
    return parseFloat(s.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.')) || 0;
  };

  const formatDate = (epoch) => {
    if (!epoch) return '';
    const d = new Date(epoch);
    return d.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' }) + ' ' + d.toLocaleTimeString('tr-TR', { timeZone: 'Europe/Istanbul', hour: '2-digit', minute: '2-digit' });
  };

  const sortData = (arr) => {
    if (!arr) return [];
    const sorted = [...arr].sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    if (search) {
      const q = search.toLowerCase();
      return sorted.filter(r => (r.name || r.range || '').toLowerCase().includes(q));
    }
    return sorted;
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const netClass = (v) => v > 0 ? 'ca-positive' : v < 0 ? 'ca-negative' : 'ca-neutral';
  const wrClass = (v) => v >= 50 ? 'ca-winrate ca-winrate-high' : 'ca-winrate ca-winrate-low';

  const toggleExpand = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
    setSubTab(prev => ({ ...prev, [key]: 'coupons' }));
    setSubExpanded({});
  };

  const filterItemsByDimension = (items, dimension, value) => {
    return items.filter(item => {
      if (dimension === 'player') {
        for (const ev of (item.events || [])) {
          const parts = (ev.name || '').split(' - ');
          for (const p of parts) { if (parsePlayerFromName(p) === value) return true; }
        }
        return false;
      } else if (dimension === 'pair') {
        for (const ev of (item.events || [])) {
          const parts = (ev.name || '').split(' - ');
          if (parts.length >= 2) {
            const h = parsePlayerFromName(parts[0]);
            const a = parsePlayerFromName(parts.slice(1).join(' - '));
            if (h && a && [h, a].sort().join(' vs ') === value) return true;
          }
        }
        return false;
      } else if (dimension === 'market') {
        for (const ev of (item.events || [])) { if (normalizeMarketFe(ev.market) === value) return true; }
        return false;
      } else if (dimension === 'odds') {
        const odd = parseFloat((item.totalOdd || '').replace(',', '.')) || 0;
        return getOddsRangeFe(odd) === value;
      } else if (dimension === 'league') {
        for (const ev of (item.events || [])) { if ((ev.league || 'Bilinmiyor') === value) return true; }
        return false;
      } else if (dimension === 'match') {
        for (const ev of (item.events || [])) { if ((ev.name || '').trim() === value) return true; }
        return false;
      }
      return false;
    });
  };

  const parsePlayerFromName = (side) => {
    const m = (side || '').trim().match(/\(([^)]+)\)$/);
    if (m && m[1].trim() !== 'K') return m[1].trim();
    return null;
  };

  const normalizeMarketFe = (name) => {
    if (!name) return 'Bilinmiyor';
    let n = name.replace(/\s*\([^)]*\)\s*$/, '').trim();
    n = n.replace(/\s+\d+[:\-]\d+\s*$/, '').trim();
    return n || name;
  };

  const getOddsRangeFe = (odd) => {
    if (odd < 1.5) return '1.00-1.49';
    if (odd < 2) return '1.50-1.99';
    if (odd < 3) return '2.00-2.99';
    if (odd < 5) return '3.00-4.99';
    if (odd < 10) return '5.00-9.99';
    if (odd < 20) return '10.00-19.99';
    return '20.00+';
  };

  const subGroupItems = (items, dimension) => {
    const groups = {};
    const add = (key, item, amount, gain) => {
      if (!groups[key]) groups[key] = { won: 0, lost: 0, spent: 0, gained: 0 };
      groups[key][item.won ? 'won' : 'lost']++;
      groups[key].spent += amount;
      groups[key].gained += gain;
    };
    for (const item of items) {
      const amount = parseTL(item.amount);
      const gain = item.won ? parseTL(item.totalGain) : 0;
      if (dimension === 'player') {
        const players = new Set();
        for (const ev of (item.events || [])) {
          const parts = (ev.name || '').split(' - ');
          for (const p of parts) { const pl = parsePlayerFromName(p); if (pl) players.add(pl); }
        }
        for (const pl of players) add(pl, item, amount, gain);
      } else if (dimension === 'pair') {
        const done = new Set();
        for (const ev of (item.events || [])) {
          const parts = (ev.name || '').split(' - ');
          if (parts.length >= 2) {
            const h = parsePlayerFromName(parts[0]);
            const a = parsePlayerFromName(parts.slice(1).join(' - '));
            if (h && a) { const pair = [h, a].sort().join(' vs '); if (!done.has(pair)) { done.add(pair); add(pair, item, amount, gain); } }
          }
        }
      } else if (dimension === 'market') {
        for (const ev of (item.events || [])) add(normalizeMarketFe(ev.market), item, amount, gain);
      } else if (dimension === 'odds') {
        const odd = parseFloat((item.totalOdd || '').replace(',', '.')) || 0;
        add(getOddsRangeFe(odd), item, amount, gain);
      } else if (dimension === 'league') {
        for (const ev of (item.events || [])) add(ev.league || 'Bilinmiyor', item, amount, gain);
      } else if (dimension === 'match') {
        const done = new Set();
        for (const ev of (item.events || [])) {
          const mn = (ev.name || '').trim();
          if (mn && !done.has(mn)) { done.add(mn); add(mn, item, amount, gain); }
        }
      }
    }
    return Object.entries(groups).map(([name, d]) => ({
      name, won: d.won, lost: d.lost, coupons: d.won + d.lost,
      spent: +d.spent.toFixed(2), gained: +d.gained.toFixed(2),
      net: +(d.gained - d.spent).toFixed(2),
      winRate: (d.won + d.lost) > 0 ? +((d.won / (d.won + d.lost)) * 100).toFixed(1) : 0,
    })).sort((a, b) => b.net - a.net);
  };

  const subTabDefs = [
    { id: 'coupons', label: '📋 Kuponlar' },
    { id: 'player', label: '👤 Oyuncu' },
    { id: 'pair', label: '🤝 Çift' },
    { id: 'market', label: '🎯 Bahis' },
    { id: 'odds', label: '📊 Oran' },
    { id: 'league', label: '🏆 Lig' },
    { id: 'match', label: '⚽ Maç' },
  ];

  const renderSubTable = (rows, parentItems, subDimension, parentKey) => (
    <div style={{ padding: '0 12px 8px' }}>
      <table className="ca-sub-table">
        <thead>
          <tr>
            <th style={{ width: 26 }}></th><th>İsim</th><th>Kupon</th><th>Kazanan</th><th>Kaybeden</th><th>Oran %</th><th>Harcanan</th><th>Kazanç</th><th>Net Kâr</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const subKey = `${parentKey}__${r.name}`;
            const isSubOpen = subExpanded[subKey];
            return (
              <React.Fragment key={i}>
                <tr className="ca-row-clickable" onClick={() => setSubExpanded(prev => ({ ...prev, [subKey]: !prev[subKey] }))}>
                  <td className="ca-expand-icon">{isSubOpen ? '▼' : '▶'}</td>
                  <td>{r.name}</td><td>{r.coupons}</td><td>{r.won}</td><td>{r.lost}</td>
                  <td className={wrClass(r.winRate)}>{r.winRate}%</td>
                  <td>{formatMoney(r.spent)} ₺</td><td>{formatMoney(r.gained)} ₺</td>
                  <td className={netClass(r.net)}>{r.net > 0 ? '+' : ''}{formatMoney(r.net)} ₺</td>
                </tr>
                {isSubOpen && (
                  <tr className="ca-sub-dropdown-row">
                    <td colSpan="9">
                      {renderCouponDropdown(filterItemsByDimension(parentItems, subDimension, r.name))}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {rows.length === 0 && <tr><td colSpan="9" style={{ textAlign: 'center', color: '#666', padding: 16 }}>Veri yok</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderCouponDropdown = (items) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="ca-dropdown">
        {items.map((item, idx) => {
          const amount = parseTL(item.amount);
          const gain = item.won ? parseTL(item.totalGain) : 0;
          const net = gain - amount;
          return (
            <div key={idx} className={`ca-dd-coupon ${item.won ? 'ca-dd-won' : 'ca-dd-lost'}`}>
              <div className="ca-dd-header">
                <span className="ca-dd-status">{item.won ? '✅' : '❌'}</span>
                {users.length > 1 && item.nesineUser && <span className="ca-dd-account">{displayNameMap[item.nesineUser] || item.nesineUser}</span>}
                <span className="ca-dd-barcode">{item.barcode}</span>
                <span className="ca-dd-date">{formatDate(item.date)}</span>
                <span className="ca-dd-odd">Oran: {item.totalOdd}</span>
                <span className="ca-dd-amount">{item.amount}</span>
                {item.won && <span className="ca-dd-gain">→ {item.totalGain}</span>}
                <span className={`ca-dd-net ${net > 0 ? 'ca-positive' : 'ca-negative'}`}>
                  {net > 0 ? '+' : ''}{formatMoney(net)} ₺
                </span>
              </div>
              <div className="ca-dd-events">
                {item.events.map((ev, ei) => (
                  <div key={ei} className="ca-dd-event">
                    <span className="ca-dd-ev-name">{ev.name}</span>
                    <span className="ca-dd-ev-market">{ev.market}</span>
                    <span className="ca-dd-ev-score">{ev.resultHalf} / {ev.result}</span>
                    {ev.outcomes.map((o, oi) => (
                      <span key={oi} className={`ca-dd-ev-outcome ${o.status === 'Win' ? 'ca-positive' : o.status === 'Loss' ? 'ca-negative' : ''}`}>
                        {o.name} ({o.odd}) {o.status === 'Win' ? '✓' : o.status === 'Loss' ? '✗' : ''}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTable = (rows, nameLabel, isOdds) => {
    const nameField = isOdds ? 'range' : 'name';
    return (
      <div className="ca-table-wrap">
        <table className="ca-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th onClick={() => handleSort(nameField)}>{nameLabel}<span className="ca-sort">{sortIcon(nameField)}</span></th>
              <th onClick={() => handleSort('coupons')}>Kupon<span className="ca-sort">{sortIcon('coupons')}</span></th>
              <th onClick={() => handleSort('won')}>Kazanan<span className="ca-sort">{sortIcon('won')}</span></th>
              <th onClick={() => handleSort('lost')}>Kaybeden<span className="ca-sort">{sortIcon('lost')}</span></th>
              <th onClick={() => handleSort('winRate')}>Oran %<span className="ca-sort">{sortIcon('winRate')}</span></th>
              <th onClick={() => handleSort('spent')}>Harcanan<span className="ca-sort">{sortIcon('spent')}</span></th>
              <th onClick={() => handleSort('gained')}>Kazanç<span className="ca-sort">{sortIcon('gained')}</span></th>
              <th onClick={() => handleSort('net')}>Net Kâr<span className="ca-sort">{sortIcon('net')}</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const key = `${activeTab}-${r.name || r.range}`;
              const isOpen = expanded[key];
              return (
                <React.Fragment key={i}>
                  <tr className="ca-row-clickable" onClick={() => toggleExpand(key)}>
                    <td className="ca-expand-icon">{isOpen ? '▼' : '▶'}</td>
                    <td>{r[nameField]}</td>
                    <td>{r.coupons}</td>
                    <td>{r.won}</td>
                    <td>{r.lost}</td>
                    <td className={wrClass(r.winRate)}>{r.winRate}%</td>
                    <td>{formatMoney(r.spent)} ₺</td>
                    <td>{formatMoney(r.gained)} ₺</td>
                    <td className={netClass(r.net)}>{r.net > 0 ? '+' : ''}{formatMoney(r.net)} ₺</td>
                  </tr>
                  {isOpen && (
                    <tr className="ca-dropdown-row">
                      <td colSpan="9">
                        <div className="ca-sub-tabs">
                          {subTabDefs.filter(st => st.id === 'coupons' || st.id !== activeTab).map(st => (
                            <button key={st.id} className={`ca-sub-tab ${(subTab[key] || 'coupons') === st.id ? 'active' : ''}`}
                              onClick={(e) => { e.stopPropagation(); setSubTab(prev => ({ ...prev, [key]: st.id })); }}>
                              {st.label}
                            </button>
                          ))}
                        </div>
                        {(subTab[key] || 'coupons') === 'coupons'
                          ? renderCouponDropdown(r.items)
                          : renderSubTable(subGroupItems(r.items, subTab[key]), r.items, subTab[key], key)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan="9" style={{ textAlign: 'center', color: '#777', padding: '30px' }}>Veri bulunamadı</td></tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const tabs = [
    { id: 'player', label: '👤 Oyuncu' },
    { id: 'pair', label: '🤝 Oyuncu Çifti' },
    { id: 'market', label: '🎯 Bahis Tipi' },
    { id: 'odds', label: '📊 Oran Aralığı' },
    { id: 'league', label: '🏆 Lig' },
    { id: 'match', label: '⚽ Maç' },
  ];

  const getActiveData = () => {
    if (!data) return [];
    switch (activeTab) {
      case 'player': return sortData(data.byPlayer);
      case 'market': return sortData(data.byMarket);
      case 'odds': return sortData(data.byOddsRange.map(r => ({ ...r, name: r.range })));
      case 'pair': return sortData(data.byPlayerPair);
      case 'league': return sortData(data.byLeague);
      case 'match': return sortData(data.byMatch);
      default: return [];
    }
  };

  const getNameLabel = () => {
    switch (activeTab) {
      case 'player': return 'Oyuncu';
      case 'pair': return 'Oyuncu Çifti';
      case 'market': return 'Bahis Tipi';
      case 'odds': return 'Oran Aralığı';
      case 'league': return 'Lig';
      case 'match': return 'Maç';
      default: return '';
    }
  };

  const s = data?.summary;

  return (
    <div className="ca-page">
      <div className="ca-header">
        <h2>📊 Kupon Analizi</h2>
      </div>

      {/* Filters */}
      <div className="ca-filters">
        <div className="ca-fg">
          <label>Başlangıç</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div className="ca-fg">
          <label>Bitiş</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
        <button className="ca-btn" onClick={fetchAnalysis} disabled={loading}>
          {loading ? '⏳ Yükleniyor...' : '🔍 Analiz Et'}
        </button>
        <button className="ca-btn" style={{ background: '#555', marginLeft: 4 }} onClick={() => { const _n = new Date(); const _yy = _n.getFullYear(), _mm = String(_n.getMonth()+1).padStart(2,'0'), _dd = String(_n.getDate()).padStart(2,'0'); setExcludeSmall(true); setRoyalOnly(false); setSelectedUsers([]); setStartDate(`${_yy}-${_mm}-01`); setEndDate(`${_yy}-${_mm}-${_dd}`); }}>
          🔄 Temizle
        </button>
        <button className={`ca-tab ${royalOnly ? 'active' : ''}`} onClick={() => { setRoyalOnly(v => !v); if (!royalOnly) setExcludeSmall(false); }} style={{ marginLeft: 4 }}>
          👑 Royal
        </button>
        {!royalOnly && (
          <button className={`ca-tab ${excludeSmall ? 'active' : ''}`} onClick={() => setExcludeSmall(v => !v)} style={{ marginLeft: 4 }}>
            🚫 Küçük Kuponlar Hariç {excludeSmall ? '(1000₺+)' : ''}
          </button>
        )}
      </div>

      {loading && <div className="ca-loading">⏳ Analiz yapılıyor...</div>}

      {!loading && data && s && (
        <>
          {/* User toggle buttons + Summary */}
          {users.length > 1 && (
            <div className="ca-user-btns">
              <button className={`ca-user-btn ${selectedUsers.length === 0 ? 'active' : ''}`} onClick={() => setSelectedUsers([])}>
                Tümü
              </button>
              {users.map(u => (
                <button key={u} className={`ca-user-btn ${selectedUsers.includes(u) ? 'active' : ''}`} onClick={() => {
                  setSelectedUsers(prev => prev.includes(u) ? prev.filter(x => x !== u) : [...prev, u]);
                }}>
                  {displayNameMap[u] || u}
                </button>
              ))}
            </div>
          )}
          <div className="ca-summary">
            <div className="ca-stat ca-stat-won">
              <div className="ca-stat-label">KAZANÇ</div>
              <div className="ca-stat-value">{formatMoney(s.totalGained)} ₺</div>
              <div className="ca-stat-sub">{s.totalWon} kupon</div>
            </div>
            <div className="ca-stat ca-stat-lost">
              <div className="ca-stat-label">TUTAR</div>
              <div className="ca-stat-value">{formatMoney(s.totalSpent)} ₺</div>
              <div className="ca-stat-sub">{s.totalCoupons} kupon</div>
            </div>
            <div className={`ca-stat ${s.netProfit >= 0 ? 'ca-stat-profit' : 'ca-stat-loss'}`}>
              <div className="ca-stat-label">KAR / ZARAR</div>
              <div className="ca-stat-value">{s.netProfit >= 0 ? '+' : ''}{formatMoney(s.netProfit)} ₺</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="ca-tabs">
            {tabs.map(t => (
              <button key={t.id} className={`ca-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => { setActiveTab(t.id); setSearch(''); setSortField('net'); setSortDir('desc'); setExpanded({}); }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <input className="ca-search" placeholder="Ara..." value={search} onChange={e => setSearch(e.target.value)} />

          {/* Table */}
          <div className="ca-section">
            {renderTable(getActiveData(), getNameLabel(), activeTab === 'odds')}
          </div>
        </>
      )}
    </div>
  );
}

export default CouponAnalysis;
